/**
 * Qué motor atiende el mostrador de facturación por QR.
 *
 * NO es un canario. El flujo mueve ~7 solicitudes al mes en 3 mostradores
 * activos, así que repartir tráfico por porcentaje no daría evidencia de nada.
 * Es un interruptor, y su valor es poder volver atrás en segundos sin
 * desplegar.
 */
export type QrEngine = 'legacy' | 'go';

/**
 * Los sub-paths son IDÉNTICOS en los dos motores: lo único que cambia es el
 * prefijo. Por eso una sola constante resuelve las 14 rutas del flujo.
 */
export const QR_ENGINE_BASE: Record<QrEngine, string> = {
  legacy: '/api/v1/heru-core-invoice-service',
  go: '/api/v1/heru-fiscal-engine',
};

/**
 * Motor forzado para probar en local sin crear el interruptor en GrowthBook:
 * `VITE_INVOICE_QR_ENGINE=go` en el `.env`. Mueve JUNTAS la página pública y la
 * del dueño, igual que el interruptor.
 *
 * Solo existe en `bun run dev`: en el build de producción `import.meta.env.DEV`
 * es `false` y Vite elimina la rama.
 */
export const devForcedQrEngine = (): QrEngine | undefined => {
  if (!import.meta.env.DEV) return undefined;
  const value = import.meta.env.VITE_INVOICE_QR_ENGINE;
  return value === 'go' || value === 'legacy' ? value : undefined;
};

/**
 * El default es el Go. El QR corre ahí desde el corte del 30-sep y el NestJS se
 * apaga, así que si el CDN de GrowthBook no responde el mostrador tiene que
 * seguir donde vive su bandeja, no regresar a un motor que ya no la atiende.
 *
 * El interruptor queda como palanca de regreso: solo manda al NestJS cuando la
 * bandera se evaluó de verdad (GrowthBook la conoce) y vino apagada. Que las
 * features «estén listas» no alcanza: `useFeaturesReady` también se pone en
 * `true` cuando el fetch falla o a los 1000 ms, y ahí la bandera es desconocida
 * y se lee `false`.
 */
export const resolveQrEngine = (
  flagEvaluated: boolean,
  goIsOn: boolean,
  forcedEngine?: QrEngine,
): QrEngine => forcedEngine ?? (flagEvaluated && !goIsOn ? 'legacy' : 'go');
